/**
 * entity-search-fields.ts
 *
 * Single source of truth for "which fields make a Deal / Fault / Job /
 * ServiceRequest match a query" — the entity-level twin of customer-search.ts.
 *
 * Used by BOTH:
 *   - the global search hook (`useGlobalSearch`) for ranked results, and
 *   - the per-entity list pages (DealsListPage, FaultPipelinePage,
 *     JobsListPage, ServiceRequestsPage) for their simple `.includes()` filter.
 *
 * Keeping the field list in one place guarantees the "I found it in the search
 * bar but not the list" (and vice-versa) class of bug can't come back: add a
 * searchable field here once and every surface picks it up.
 *
 * Phone fields are returned separately (digit-aware matching lives in
 * search-ranking.ts `scorePhoneMatch`).
 */

import type { Job } from '@/types/schema';
import type { Deal } from '@/types/theo-workflow.types';
import type { Fault } from '@/types/fault.types';
import type { ServiceRequest } from '@/types/service-request.types';
import { scorePhoneMatch } from './search-ranking';

type Field = string | undefined | null;

// ── Deals ────────────────────────────────────────────────────────────────────

export function dealSearchFields(d: Deal): Field[] {
  return [
    d.customerName,
    d.title,
    d.notes,
    d.nextAction,
    d.accountContactName,
    d.siteContactName,
    d.contactName,
    d.jobAddress,
    // Firestore doc id — searchable uniformly across deals/quotes/global.
    d.id,
    // Stage label is searchable ("quoting", "won", "billing", …).
    (d.stage || '').toString(),
  ];
}

export function dealPhoneFields(d: Deal): Field[] {
  return [d.accountContactPhone, d.siteContactPhone];
}

// ── Faults ───────────────────────────────────────────────────────────────────

export function faultSearchFields(f: Fault): Field[] {
  return [
    // Ticket number ("FLT-0017") — what the office quotes in Slack/on calls.
    f.ticketNumber,
    f.clientName,
    f.symptom,
    f.address,
    f.serviceType,
    f.servicePlanName,
    f.intakeNotes,
    f.deviceInfo?.model,
    f.deviceInfo?.serialNumber,
    f.deviceInfo?.location,
    f.severity,
    f.stage,
  ];
}

// ── Jobs ─────────────────────────────────────────────────────────────────────

export function jobSearchFields(j: Job): Field[] {
  return [
    j.client,
    j.address,
    j.loc,
    j.notes,
    j.criticalNotes,
    j.linkedCustomerName,
    j.contactName,
    j.accountContactName,
    j.siteContactName,
    j.type,
    j.status,
    j.id,
    j.dealCategory,
    (j as Job & { techName?: string }).techName,
    ...((j.services || []) as string[]),
    // Serial numbers stamped on planned materials (see serial-number.service).
    ...((j.plannedMaterials || []).map(
      (m) => (m as { serialNumber?: string }).serialNumber,
    ) as Field[]),
  ];
}

export function jobPhoneFields(j: Job): Field[] {
  return [
    (j as Job & { contact?: string }).contact,
    j.accountContactPhone,
    j.siteContactPhone,
  ];
}

// ── Service Requests ─────────────────────────────────────────────────────────

export function serviceRequestSearchFields(r: ServiceRequest): Field[] {
  return [
    // Ticket number ("SR-0042") — what the office quotes in Slack/on calls.
    r.ticketNumber,
    r.customerName,
    r.title,
    r.description,
    r.serviceType,
    r.servicePlanName,
    r.priority,
    r.stage,
    r.requestType,
    r.assignedToName,
  ];
}

/**
 * Convenience boolean matcher for the list-page `.filter()` surfaces — case-
 * insensitive substring across a pre-built field array. The query should be
 * pre-trimmed + lowercased by the caller (mirrors the list pages).
 */
export function fieldsMatchQuery(fields: Field[], loweredQuery: string): boolean {
  if (!loweredQuery) return true;
  for (const f of fields) {
    if (f && String(f).toLowerCase().includes(loweredQuery)) return true;
  }
  return false;
}

function anyPhoneMatches(phones: Field[], digitsOnly: string): boolean {
  if (digitsOnly.length < 4) return false;
  for (const p of phones) {
    if (scorePhoneMatch(digitsOnly, p) > 0) return true;
  }
  return false;
}

/**
 * Boolean matcher for the DEAL list pages — text fields PLUS digit-aware phone
 * matching, so a deal is findable by its contact phone on the list exactly as
 * it is in global search (which the naive text-only fieldsMatchQuery could not
 * do). `digitsOnly` is the query stripped to digits.
 */
export function dealMatchesQuery(d: Deal, loweredQuery: string, digitsOnly: string): boolean {
  return (
    fieldsMatchQuery(dealSearchFields(d), loweredQuery) ||
    anyPhoneMatches(dealPhoneFields(d), digitsOnly)
  );
}

/** Boolean matcher for the JOB list page — text fields PLUS digit-aware phone. */
export function jobMatchesQuery(j: Job, loweredQuery: string, digitsOnly: string): boolean {
  return (
    fieldsMatchQuery(jobSearchFields(j), loweredQuery) ||
    anyPhoneMatches(jobPhoneFields(j), digitsOnly)
  );
}
